import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, BACKEND_URL } from "./api";

// Default fallbacks — empty strings mean "use text/icon-based fallback in components"
export const DEFAULT_BRAND = {
  logo_full_url: "",
  logo_mark_url: "",
  wallpaper_url: "",
  favicon_url: "",
};

export const DEFAULT_CLUB = {
  name: "Nordgaming e.V.",
  tagline: "Play beyond limits",
  primary_game: "EA FC 26",
};

// Resolve relative /uploads/... URLs to absolute backend URLs so they work in dev/preview
export function resolveAssetUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
}

const BrandContext = createContext({
  branding: DEFAULT_BRAND,
  club: DEFAULT_CLUB,
  refresh: () => {},
  // resolved URLs for direct use:
  LOGO_FULL: null,
  LOGO_MARK: null,
  WALLPAPER: null,
  FAVICON: null,
  TAGLINE: DEFAULT_CLUB.tagline,
  CLUB_NAME: DEFAULT_CLUB.name,
});

export function BrandProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRAND);
  const [club, setClub] = useState(DEFAULT_CLUB);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      if (data?.branding) setBranding({ ...DEFAULT_BRAND, ...data.branding });
      if (data?.club) setClub({ ...DEFAULT_CLUB, ...data.club });
    } catch (e) {
      // fall back to defaults
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep favicon + page title in sync with branding
  useEffect(() => {
    const favUrl = resolveAssetUrl(branding.favicon_url || branding.logo_mark_url);
    if (favUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = favUrl;
      let touch = document.querySelector("link[rel='apple-touch-icon']");
      if (!touch) {
        touch = document.createElement("link");
        touch.rel = "apple-touch-icon";
        document.head.appendChild(touch);
      }
      touch.href = favUrl;
    }
    if (club?.name && club?.tagline) {
      document.title = `${club.name} Portal · ${club.tagline}`;
    }
  }, [branding, club]);

  const value = {
    branding,
    club,
    refresh: load,
    LOGO_FULL: resolveAssetUrl(branding.logo_full_url),
    LOGO_MARK: resolveAssetUrl(branding.logo_mark_url),
    WALLPAPER: resolveAssetUrl(branding.wallpaper_url),
    FAVICON: resolveAssetUrl(branding.favicon_url || branding.logo_mark_url),
    TAGLINE: club.tagline,
    CLUB_NAME: club.name,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export const useBrand = () => useContext(BrandContext);
